/**
 * The unread badge: one count, shown three ways.
 *
 * - The count is Discord's red number: mentions in servers, unread DMs (a DM
 *   counts its messages) and friend requests waiting for an answer. Someone
 *   you ignore never adds to it.
 * - The web: "(3) Neosocial" in the tab's title and a red dot with the number
 *   on the favicon.
 * - The desktop app: the same red dot as the taskbar button's overlay icon
 *   (the shell's `setBadge`, drawn here so both look alike).
 *
 * "Taskbar badge" off (Settings > Notifications) hides all three.
 */
import type { Snowflake } from '@neosocial/protocol';
import type { DataState } from '../store/reducer';
import { dmUnread, pendingIncoming } from '../store/selectors';

/** The badge's number. */
export function unreadBadgeCount(s: DataState, ignored: Record<Snowflake, true> = {}): number {
  let n = 0;
  for (const c of Object.values(s.channels)) {
    if (c.guild_id) n += s.readStates[c.id]?.mention_count ?? 0;
  }
  return n + dmUnread(s, ignored) + pendingIncoming(s);
}

/** What fits in the dot: "9+" past nine. */
export function badgeLabel(n: number): string {
  return n > 9 ? '9+' : String(Math.max(0, Math.floor(n)));
}

const RED = '#e5484d';

/** jsdom (the tests) has no canvas; a real browser and WebView2 do. */
function canDraw(): boolean {
  if (typeof document === 'undefined' || typeof navigator === 'undefined') return false;
  return !/jsdom/i.test(navigator.userAgent);
}

function canvas(size: number): [HTMLCanvasElement, CanvasRenderingContext2D] | null {
  if (!canDraw()) return null;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d');
  return g ? [c, g] : null;
}

/** The red dot with its number, centred on (x, y). */
function dot(g: CanvasRenderingContext2D, x: number, y: number, r: number, label: string): void {
  g.beginPath();
  g.arc(x, y, r, 0, Math.PI * 2);
  g.fillStyle = RED;
  g.fill();
  g.fillStyle = '#fff';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.font = `700 ${Math.round(r * (label.length > 1 ? 1.05 : 1.3))}px system-ui, "Segoe UI", sans-serif`;
  g.fillText(label, x, y + r * 0.08);
}

/** The taskbar overlay: `size` x `size` RGBA pixels, or null where nothing can draw. */
export function badgeRgba(n: number, size = 32): Uint8ClampedArray | null {
  const cv = canvas(size);
  if (!cv) return null;
  const [, g] = cv;
  dot(g, size / 2, size / 2, size / 2, badgeLabel(n));
  return g.getImageData(0, 0, size, size).data;
}

/* ---------------------------------------------------------------- favicon */

let original: { href: string; type: string } | null = null;
let icon: Promise<HTMLImageElement | null> | null = null;
let plainPng: string | null = null;
let seq = 0;

function faviconLink(): HTMLLinkElement | null {
  return typeof document === 'undefined'
    ? null
    : document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
}

function loadIcon(href: string): Promise<HTMLImageElement | null> {
  const img = new Image();
  img.src = href;
  return img.decode().then(
    () => img,
    () => null,
  );
}

/** The app's icon drawn at 64 px (raster: Chrome's notifications show no SVG icon). */
async function iconCanvas(): Promise<[HTMLCanvasElement, CanvasRenderingContext2D] | null> {
  const link = faviconLink();
  if (!link) return null;
  original ??= { href: link.href, type: link.type };
  const img = await (icon ??= loadIcon(original.href));
  const cv = canvas(64);
  if (!cv) return null;
  if (img) cv[1].drawImage(img, 0, 0, 64, 64);
  return cv;
}

/** The app icon as a PNG data URL for web notifications (null until it could be drawn). */
export function appIconPng(): string | null {
  if (plainPng === null && canDraw())
    void iconCanvas().then((cv) => {
      if (cv) plainPng = cv[0].toDataURL('image/png');
    });
  return plainPng;
}

/** The favicon with the red dot (0 puts the plain icon back). */
export async function renderFavicon(n: number): Promise<void> {
  const link = faviconLink();
  if (!link) return;
  original ??= { href: link.href, type: link.type };
  const mine = ++seq;
  if (n <= 0) {
    link.type = original.type;
    link.href = original.href;
    return;
  }
  const cv = await iconCanvas();
  if (!cv || mine !== seq) return;
  const [c, g] = cv;
  plainPng ??= c.toDataURL('image/png');
  // a ring of transparency around the dot, as on the app's own badges
  g.globalCompositeOperation = 'destination-out';
  g.beginPath();
  g.arc(46, 46, 21, 0, Math.PI * 2);
  g.fill();
  g.globalCompositeOperation = 'source-over';
  dot(g, 46, 46, 18, badgeLabel(n));
  link.type = 'image/png';
  link.href = c.toDataURL('image/png');
}
